import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSubdomainInfo } from '../utils/subdomain';
import api from '../services/api';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [subdomainState, setSubdomainState] = useState<'checking' | 'valid' | 'invalid' | 'notfound'>('checking');
  const [cityName, setCityName] = useState('');
  const [citizenRegistrationEnabled, setCitizenRegistrationEnabled] = useState(true);
  const { login } = useAuth();

  const subdomain = getSubdomainInfo();

  useEffect(() => {
    if (subdomain) {
      api.get('/cities/resolve', { params: { city: subdomain.citySlug, state: subdomain.stateUf } })
        .then((res) => {
          setCityName(res.data.name);
          setCitizenRegistrationEnabled(res.data.citizenRegistrationEnabled !== false);
          setSubdomainState('valid');
        })
        .catch((err) => {
          const msg = err?.response?.data?.message || '';
          if (msg === 'Página não encontrada') {
            setSubdomainState('notfound');
          } else {
            setSubdomainState('invalid');
          }
        });
    } else {
      setSubdomainState('valid');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Email ou senha inválidos');
    } finally {
      setLoading(false);
    }
  };

  if (subdomainState === 'checking') return null;

  if (subdomainState === 'notfound') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full p-8 bg-white rounded-lg shadow-md text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">GeoZ</h1>
          <p className="text-4xl font-bold text-gray-300 mb-2">404</p>
          <p className="text-gray-500 font-medium">Página não encontrada</p>
        </div>
      </div>
    );
  }

  if (subdomainState === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full p-8 bg-white rounded-lg shadow-md text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">GeoZ</h1>
          <p className="text-red-600 font-medium">Cidade não possui o serviço</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">GeoZ</h1>
          <p className="mt-2 text-gray-600">Gestão de Zeladoria</p>
          {subdomain && cityName && (
            <p className="mt-1 text-sm font-medium text-primary-600">
              {cityName} - {subdomain.stateUf}
            </p>
          )}
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Senha
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>

          <div className="text-right">
            <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700">
              Esqueci minha senha
            </Link>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50">
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
        {citizenRegistrationEnabled && (
          <p className="text-sm text-gray-500 text-center">
            Não tem conta?{' '}
            <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">Cadastre-se</Link>
          </p>
        )}
      </div>
    </div>
  );
}